("use client");

import { useCallback, useMemo } from "react";
import { useColumn } from "@/hooks/use-column";
import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import { getColumns } from "./columns";
import useApi from "@/hooks/useApi";
import PlacesRepository from "@/utilities/repositories/Places";
import { IPlace } from "@/utilities/types/Places";
import CreateEditPlace from "../create-edit/create-edit-place";
import ImportCSVModal from "../import-csv-modal";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { Title } from "rizzui";
import ModalButton from "@/app/shared/modal-button";
import { PiUploadSimple } from "react-icons/pi";

export default function PlacesTable() {
  const { request: removePlace } = useApi({
    request: PlacesRepository.deletePlaces,
    enableToast: true,
  });

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleReset,
    handleChangePageSize,
  } = useTableServer<IPlace>({
    server: {
      request: PlacesRepository.getPlaces,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onDeleteItem = useCallback(
    async (id: string) => {
      await removePlace(id);
      handlePaginate(currentPage); // Recall API
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handlePaginate, currentPage]
  );

  const { openModal } = useModal();
  const onEditItem = (row: IPlace) => {
    openModal({
      view: (
        <CreateEditPlace
          refetchData={() => handlePaginate(currentPage)}
          data={row}
        />
      ),
      customSize: "500px",
    });
  };

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        onEditItem,
        onDeleteItem,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditItem, onDeleteItem, JSON.stringify(tableData)]
  );

  const { visibleColumns } = useColumn(columns);

  return (
    <>
      <div className="mb-4 w-full flex items-center justify-between">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          Places
        </Title>

        <div className="flex items-center gap-3">
          <ModalButton
            view={<ImportCSVModal refetchData={handleReset} />}
            label="Import CSV"
            customSize="800px"
            className="mt-0 w-auto"
          >
            <PiUploadSimple className="mr-2 h-4 w-4" />
            Import CSV
          </ModalButton>
          
          <ModalButton
            view={<CreateEditPlace refetchData={handleReset} />}
            label="Add Place"
            customSize="500px"
            className="mt-0 w-auto"
          />
        </div>
      </div>

      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-ignore
        columns={visibleColumns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </>
  );
}
